import { Box, IconButton, InputBase, makeStyles } from '@material-ui/core';
import { FC, MouseEventHandler, ReactNode } from 'react';

import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { getFocusVisibleStyle } from '../../styles/focus';

interface Props {
  children?: ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement> | undefined;
  onClear: () => void;
  isOpened: boolean;
  maxWidth?: string;
  selected?: boolean;
  hideClear?: boolean;
  placeholder?: string;
  field?: string;
}

type StyleProps = {
  maxWidth: string;
  selected?: boolean;
  isOpened: boolean;
};

const useStyles = makeStyles(
  theme => ({
    root: (props: StyleProps) => ({
      position: 'relative',
      maxWidth: props.maxWidth,
      height: '36px',
      borderRadius: '4px',
      padding: '6px 8px 6px 12px',
      border: `1px solid ${theme.palette.grey[400]}`,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      cursor: 'pointer',
      background: Boolean(props.selected)
        ? theme.palette.grey[100]
        : theme.palette.background.default,
      '&:has($expandButton:focus-visible)': {
        ...getFocusVisibleStyle(theme),
        '& $expandButton': {
          outline: 'none',
        },
      },
    }),
    placeholderInput: {
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      cursor: 'pointer',
      '&::placeholder': {
        color: theme.palette.secondary.light,
        opacity: 1,
      },
    },
    endActions: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    divider: {
      width: '1px',
      height: '20px',
      background: theme.palette.grey[300],
      margin: '0px 5px',
    },
    expandButton: (props: StyleProps) => ({
      transition: 'all 0.3s',
      transform: props.isOpened ? 'rotate(180deg)' : 'rotate(-90deg)',
    }),
  }),
  { name: 'FilterInput' },
);

export const FilterInput: FC<Props> = ({
  onClick,
  onClear,
  isOpened,
  maxWidth = 'none',
  selected,
  children,
  hideClear,
  placeholder,
  field,
}) => {
  const classes = useStyles({ maxWidth, selected, isOpened });

  return (
    <Box className={classes.root} onClick={onClick}>
      {placeholder && !selected ? (
        <InputBase
          placeholder={placeholder}
          onMouseDown={e => {
            e.preventDefault();
          }}
          inputProps={{
            className: classes.placeholderInput,
            'aria-label': placeholder,
            readOnly: true,
            tabIndex: -1,
          }}
        />
      ) : (
        children
      )}

      <Box className={classes.endActions}>
        {selected && (
          <>
            {!hideClear && (
              <IconButton
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
                size="small"
                aria-label="clear filter"
                disableFocusRipple
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}

            <Box className={classes.divider} />
          </>
        )}

        <IconButton
          className={classes.expandButton}
          onClick={onClick}
          size="small"
          aria-label={
            isOpened ? `close ${field} filter` : `open ${field} filter`
          }
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
